/**
 * Suggest an industry (`POST /api/industries/suggest`, `industry.suggest`). A new name waits for an industry manager's
 * review and can be tagged once approved; a name that exists already comes back as it is and, when approved and
 * active, is tagged at once.
 */

import { useState } from 'react';

import { IndustrySuggestIn, industrySuggestInNameMax } from '@/api/generated/schemas';
import { useSuggestIndustry } from '@/api/queries';
import type { Industry } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

export function SuggestIndustryDialog({ onClose, onSuggested }: { onClose: () => void; onSuggested: (industry: Industry) => void }) {
  const suggest = useSuggestIndustry();
  const [name, setName] = useState('');
  const invalid = Object.keys(fieldErrors(IndustrySuggestIn, { name })).length > 0;
  const error = serverFieldErrors(suggest.error).name;

  return (
    <Modal
      title="Suggest an industry"
      subtitle="A new industry waits for review before assets can be tagged with it."
      size="narrow"
      onClose={onClose}
      onSubmit={() =>
        suggest.mutate(name.trim(), {
          onSuccess: (industry) => {
            onSuggested(industry);
            onClose();
          },
        })
      }
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(suggest)} label="Suggest" pendingLabel="Sending…" disabled={invalid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(suggest.error, Object.keys(IndustrySuggestIn.shape))} />
        <div className="field">
          <label htmlFor="suggest-industry-name">Industry name</label>
          <input id="suggest-industry-name" type="text" required autoFocus maxLength={industrySuggestInNameMax} value={name} {...fieldA11y('suggest-industry-name', error)} onChange={(e) => setName(e.target.value)} />
          <FieldError inputId="suggest-industry-name" message={error} />
        </div>
      </div>
    </Modal>
  );
}
